// ============================================
// pages/Salon/SalonSettings.js
// Salon Settings — Admin PIN, Email OTP, Report Visibility Controls
// ============================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';
import {
  Shield, Mail, KeyRound, Eye, EyeOff, Lock, Unlock,
  CheckCircle2, XCircle, Loader2,
  BarChart3, TrendingUp, Wallet, Users, RotateCcw,
  Trash2, AlertTriangle, Send, RefreshCw, Save, Building2, UserRound, Phone, X, Globe,
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SalonUserAccessSection from '../components/SalonUserAccessSection';
import { SALON_ROLE_DEFINITIONS } from '../../config/salonPermissions';

// ─── Report page options ─────────────────────────────────────────────────
const REPORT_PAGES = [
  { id: 'sales',     label: 'Sales Report',     icon: TrendingUp, desc: 'Revenue, bills, discounts, top services & products' },
  { id: 'expenses',  label: 'Expense Report',   icon: Wallet,     desc: 'Total spending, category breakdown, recent transactions' },
  { id: 'attendant', label: 'Attendant Report', icon: Users,      desc: 'Staff performance, services, revenue by attendant' },
];

const DEFAULT_REPORT_ACCESS = {
  sales: { visibility: 'everyone', allowedRoles: [], requirePin: false },
  expenses: { visibility: 'everyone', allowedRoles: [], requirePin: false },
  attendant: { visibility: 'everyone', allowedRoles: [], requirePin: false },
};

const REPORT_ROLE_OPTIONS = ['manager', 'cashier', 'receptionist', 'marketing', 'staff'].map((role) => ({
  id: role,
  label: SALON_ROLE_DEFINITIONS[role]?.label || role,
}));

// ─── OTP Input ──────────────────────────────────────────────────────────
function OtpInput({ length = 6, value, onChange }) {
  const inputs = useRef([]);
  const vals   = value.split('');
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !vals[i] && i > 0) inputs.current[i-1]?.focus();
  };
  const handleChange = (i, v) => {
    const digit = v.replace(/\D/g, '').slice(-1);
    const next  = [...vals];
    next[i] = digit;
    onChange(next.join(''));
    if (digit && i < length - 1) inputs.current[i+1]?.focus();
  };
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length }).map((_, i) => (
        <input key={i}
          ref={el => inputs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1}
          value={vals[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKey(i, e)}
          className={`w-11 h-12 text-center text-lg font-black rounded-xl border-2 transition-all focus:outline-none
            ${vals[i] ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-gray-50 text-gray-800'}
            focus:border-purple-500 focus:ring-2 focus:ring-purple-100`}
        />
      ))}
    </div>
  );
}

// ─── PIN Input ──────────────────────────────────────────────────────────
function PinInput({ label, value, onChange, show, onToggle, placeholder = '••••', maxLength = 4 }) {
  return (
    <div>
      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, maxLength))}
          placeholder={placeholder}
          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-sm font-bold tracking-widest focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 text-gray-800 pr-12"
        />
        <button type="button" onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
          {show ? <EyeOff size={16}/> : <Eye size={16}/>}
        </button>
      </div>
    </div>
  );
}

// ─── PinBoxes Input ──────────────────────────────────────────────────────
function PinBoxes({ value, onChange, color = 'blue' }) {
  const refs = useRef([]);
  const handleChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const next = [...value]; next[i] = v.slice(-1); onChange(next);
    if (v && i < 3) refs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
  };
  return (
    <div className="flex gap-3 justify-center">
      {value.map((d, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl outline-none transition-all
            ${d ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200'} focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
        />
      ))}
    </div>
  );
}

const MANAGER_FEATURES = [
  { id: 'sales_reports', label: 'Sales Reports' },
  { id: 'expense_reports', label: 'Expense Reports' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'billing', label: 'Billing' },
  { id: 'employee_management', label: 'Employee Management' },
  { id: 'user_management', label: 'User Management' },
  { id: 'settings', label: 'Settings' },
  { id: 'analytics', label: 'Analytics' },
];

// ─── Section wrapper ─────────────────────────────────────────────────────
function Section({ title, desc, icon: Icon, color = 'purple', children }) {
  const cls = { purple: 'text-purple-500', blue: 'text-blue-500', red: 'text-red-500', orange: 'text-orange-500' };
  return (
    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs hover:shadow-sm transition-shadow duration-300">
      <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-850">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl bg-slate-50/50 dark:bg-slate-800 flex items-center justify-center`}>
            <Icon size={18} className={cls[color] || cls.purple}/>
          </div>
          <div>
            <h2 className="font-black text-gray-900 dark:text-white text-base">{title}</h2>
            {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Toggle row ──────────────────────────────────────────────────────────
function ToggleRow({ icon: Icon, label, desc, checked, onChange, disabled }) {
  return (
    <div className={`flex items-center justify-between py-3 px-4 rounded-2xl transition-colors ${checked ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-gray-50 dark:bg-slate-700/50'} ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-3">
        <Icon size={16} className={checked ? 'text-purple-500' : 'text-gray-400'}/>
        <div>
          <p className="text-sm font-bold text-gray-800 dark:text-white">{label}</p>
          {desc && <p className="text-xs text-gray-400">{desc}</p>}
        </div>
      </div>
      <button
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0
          ${checked ? 'bg-purple-600' : 'bg-gray-300 dark:bg-slate-600'}`}
      >
        <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-0'}`}/>
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────
export default function SalonSettings() {
  const { userToken: token, isSalonAdmin, updateUserData, userData, language, updateUserLanguage } = useAuth();
  const isOwner = !!userData?.isOwner;
  const [activeTab, setActiveTab] = useState('profile');

  const handleLanguageChange = async (lang) => {
    const success = await updateUserLanguage(lang);
    if (success) {
      toast.success('Language settings updated successfully');
    } else {
      toast.error('Failed to update language settings');
    }
  };

  // ── Salon profile ──────────────────────────────────────────────────────
  const [salonProfile, setSalonProfile] = useState({
    salonName: '',
    contactPersonName: '',
    contactPhone: '',
    logoUrl: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // ── Admin email & OTP ─────────────────────────────────────────────────
  const [adminEmail,     setAdminEmail]     = useState('');
  const [savedEmail,     setSavedEmail]     = useState('');
  const [emailOtp,       setEmailOtp]       = useState('');
  const [otpSent,        setOtpSent]        = useState(false);
  const [otpVerified,    setOtpVerified]    = useState(false);
  const [sendingOtp,     setSendingOtp]     = useState(false);
  const [verifyingOtp,   setVerifyingOtp]   = useState(false);
  const [otpCooldown,    setOtpCooldown]    = useState(0);

  // ── Admin PIN ─────────────────────────────────────────────────────────
  const [hasAdminPin,      setHasAdminPin]      = useState(false);
  const [newPin,           setNewPin]           = useState('');
  const [confirmPin,       setConfirmPin]       = useState('');
  const [showPin,          setShowPin]          = useState(false);
  const [showConfirm,      setShowConfirm]      = useState(false);
  const [savingPin,        setSavingPin]        = useState(false);
  const [reportPin,        setReportPin]        = useState('');
  const [confirmReportPin, setConfirmReportPin] = useState('');
  const [savingReportPin,  setSavingReportPin]  = useState(false);
  // OTP gate for updating an existing PIN
  const [updateOtpSent,    setUpdateOtpSent]    = useState(false);
  const [updateOtpVerified,setUpdateOtpVerified]= useState(false);
  const [updateOtp,        setUpdateOtp]        = useState('');
  const [sendingUpdateOtp, setSendingUpdateOtp] = useState(false);
  const [verifyingUpdateOtp,setVerifyingUpdateOtp]=useState(false);
  const [updateOtpCooldown,setUpdateOtpCooldown]= useState(0);

  // ── Report visibility ─────────────────────────────────────────────────
  const [reportAccess, setReportAccess] = useState(DEFAULT_REPORT_ACCESS);
  const [savingReports,    setSavingReports]     = useState(false);
  // PIN gate for visibility changes (when not logged in as admin)
  const [visibilityUnlocked,  setVisibilityUnlocked]  = useState(() => {
    try {
      const savedTime = localStorage.getItem('salonAdminPinUnlockedTime');
      if (savedTime) {
        const elapsed = Date.now() - parseInt(savedTime, 10);
        if (elapsed < 12 * 60 * 60 * 1000) return true; // 12 hours
      }
    } catch (e) {}
    return false;
  });
  const [showVisibilityPin,   setShowVisibilityPin]    = useState(false); // modal open
  const [visibilityPin,       setVisibilityPin]        = useState(['', '', '', '']);
  const [verifyingVisPin,     setVerifyingVisPin]      = useState(false);
  const [visPinError,         setVisPinError]          = useState('');

  // ── Manager Permissions Settings ──────────────────────────────────────
  const [managerFullAccess, setManagerFullAccess] = useState(true);
  const [restrictedFeatures, setRestrictedFeatures] = useState([]);
  const [savingManagerPerms, setSavingManagerPerms] = useState(false);
  const [showManagerVerifyModal, setShowManagerVerifyModal] = useState(false);
  const [managerVerifyPin, setManagerVerifyPin] = useState(['', '', '', '']);
  const [managerVerifyOtp, setManagerVerifyOtp] = useState('');
  const [managerVerifyOtpSent, setManagerVerifyOtpSent] = useState(false);
  const [managerVerifyLoading, setManagerVerifyLoading] = useState(false);
  const [managerVerifyError, setManagerVerifyError] = useState('');
  const [onVerifiedCallback, setOnVerifiedCallback] = useState(null);

  const triggerVerification = (callback) => {
    setManagerVerifyPin(['', '', '', '']);
    setManagerVerifyOtp('');
    setManagerVerifyOtpSent(false);
    setManagerVerifyError('');
    setOnVerifiedCallback(() => callback);
    setShowManagerVerifyModal(true);
  };

  const handleSaveManagerPermissions = async (credentials) => {
    setSavingManagerPerms(true);
    try {
      const res = await fetch(`${SALON_API_URL}/admin-settings/save-manager-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          managerFullAccess,
          restrictedFeatures,
          pin: credentials.pin,
          otp: credentials.otp
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Settings saved successfully. The manager cannot access the selected features.');
        await updateUserData({
          managerFullAccess,
          restrictedFeatures
        });
      } else {
        throw new Error(data.message || 'Failed to save settings');
      }
    } catch (err) {
      toast.error(err.message || 'Error saving settings');
    } finally {
      setSavingManagerPerms(false);
    }
  };

  const handleManagerVerifyAction = async () => {
    setManagerVerifyLoading(true);
    setManagerVerifyError('');
    try {
      const pinStr = managerVerifyPin.join('');
      const credentials = {};
      if (pinStr.length === 4) {
        credentials.pin = pinStr;
      } else if (managerVerifyOtp.trim()) {
        credentials.otp = managerVerifyOtp.trim();
      } else {
        setManagerVerifyError('Enter Admin PIN or OTP code to verify');
        setManagerVerifyLoading(false);
        return;
      }

      if (onVerifiedCallback) {
        await onVerifiedCallback(credentials);
      }
      setShowManagerVerifyModal(false);
    } catch (err) {
      setManagerVerifyError(err.message || 'Verification failed');
    } finally {
      setManagerVerifyLoading(false);
    }
  };

  const handleSendManagerVerifyOtp = async () => {
    if (!savedEmail) {
      setManagerVerifyError('No admin email set to receive OTP');
      return;
    }
    setManagerVerifyLoading(true);
    try {
      const res = await fetch(`${SALON_API_URL}/admin-settings/send-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: savedEmail, purpose: 'verify' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setManagerVerifyOtpSent(true);
        toast.success(`Verification code sent to ${savedEmail}`);
      } else {
        setManagerVerifyError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setManagerVerifyError('Network error. Please try again.');
    } finally {
      setManagerVerifyLoading(false);
    }
  };

  const toggleFeatureRestriction = (featureId) => {
    setRestrictedFeatures(prev => {
      if (prev.includes(featureId)) {
        return prev.filter(id => id !== featureId);
      } else {
        return [...prev, featureId];
      }
    });
  };

  // ── Delete / Reset ────────────────────────────────────────────────────
  const [deleteMode,     setDeleteMode]     = useState(null); // null | 'reset' | 'delete'
  const [deleteOtp,      setDeleteOtp]      = useState('');
  const [deleteOtpSent,  setDeleteOtpSent]  = useState(false);
  const [deletingOtp,    setDeletingOtp]    = useState(false);
  const [deleteCooldown, setDeleteCooldown] = useState(0);
  const [confirming,     setConfirming]     = useState(false);

  const [loading, setLoading] = useState(true);

  // ── Countdown timer helper ─────────────────────────────────────────────
  const startCooldown = (setter, seconds = 60) => {
    setter(seconds);
    const t = setInterval(() => setter(p => { if (p <= 1) { clearInterval(t); return 0; } return p - 1; }), 1000);
  };

  // ── Fetch current settings ─────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    if (!token) return;
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && data.data) {
        setSavedEmail(data.data.adminEmail || '');
        setAdminEmail(data.data.adminEmail || '');
        setHasAdminPin(data.data.hasAdminPin || false);
        setReportAccess(data.data.reportAccess || DEFAULT_REPORT_ACCESS);
        setManagerFullAccess(data.data.managerFullAccess !== false);
        setRestrictedFeatures(data.data.restrictedFeatures || []);
        setSalonProfile({
          salonName: data.data.salonProfile?.salonName || userData?.salonName || 'My Salon',
          contactPersonName: data.data.salonProfile?.contactPersonName || '',
          contactPhone: data.data.salonProfile?.contactPhone || '',
          logoUrl: data.data.salonProfile?.logoUrl || '',
        });
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, userData?.salonName]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSalonProfile = async () => {
    if (!salonProfile.salonName.trim()) {
      toast.error('Salon name is required');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await fetch(SALON_API_URL + '/admin-settings/profile', {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer ' + token },
        body: JSON.stringify(salonProfile),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Failed to save salon info');
        return;
      }
      setSalonProfile({
        salonName: data.data.salonProfile?.salonName || salonProfile.salonName,
        contactPersonName: data.data.salonProfile?.contactPersonName || '',
        contactPhone: data.data.salonProfile?.contactPhone || '',
        logoUrl: data.data.salonProfile?.logoUrl || salonProfile.logoUrl || '',
      });
      await updateUserData({
        salonName: data.data.salonProfile?.salonName || salonProfile.salonName,
        salonContactName: data.data.salonProfile?.contactPersonName || '',
        salonContactPhone: data.data.salonProfile?.contactPhone || '',
        logoUrl: data.data.salonProfile?.logoUrl || salonProfile.logoUrl || '',
      });
      toast.success('Salon info saved');
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Send OTP for email verification ───────────────────────────────────
  const sendEmailOtp = async () => {
    if (!adminEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
      toast.error('Enter a valid email address first'); return;
    }
    setSendingOtp(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/send-otp`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ email: adminEmail, purpose: 'verify_email' }),
      });
      const data = await res.json();
      if (data.success) { setOtpSent(true); setEmailOtp(''); startCooldown(setOtpCooldown); toast.success(`OTP sent to ${adminEmail}`); }
      else toast.error(data.message || 'Failed to send OTP');
    } catch(e) { toast.error('Network error'); }
    finally { setSendingOtp(false); }
  };

  // ── Verify OTP ────────────────────────────────────────────────────────
  const verifyEmailOtp = async () => {
    if (emailOtp.length < 6) { toast.error('Enter the 6-digit OTP'); return; }
    setVerifyingOtp(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/verify-otp`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ email: adminEmail, otp: emailOtp, purpose: 'verify_email' }),
      });
      const data = await res.json();
      if (data.success) { setOtpVerified(true); setSavedEmail(adminEmail); toast.success('Email verified ✓'); }
      else toast.error(data.message || 'Wrong OTP');
    } catch(e) { toast.error('Network error'); }
    finally { setVerifyingOtp(false); }
  };

  // ── Send OTP for PIN update (when PIN already exists) ────────────────
  const sendUpdateOtp = async () => {
    if (!savedEmail) { toast.error('No admin email found'); return; }
    setSendingUpdateOtp(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/send-otp`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ email: savedEmail, purpose: 'update_pin' }),
      });
      const data = await res.json();
      if (data.success) {
        setUpdateOtpSent(true); setUpdateOtp('');
        startCooldown(setUpdateOtpCooldown);
        toast.success(`OTP sent to ${savedEmail}`);
      } else toast.error(data.message || 'Failed to send OTP');
    } catch(e) { toast.error('Network error'); }
    finally { setSendingUpdateOtp(false); }
  };

  // ── Verify OTP for PIN update ─────────────────────────────────────────
  const verifyUpdateOtp = async () => {
    if (updateOtp.length < 6) { toast.error('Enter the 6-digit OTP'); return; }
    setVerifyingUpdateOtp(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/verify-otp`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ email: savedEmail, otp: updateOtp, purpose: 'update_pin' }),
      });
      const data = await res.json();
      if (data.success) { setUpdateOtpVerified(true); toast.success('OTP verified ✓'); }
      else toast.error(data.message || 'Wrong OTP');
    } catch(e) { toast.error('Network error'); }
    finally { setVerifyingUpdateOtp(false); }
  };

  // ── Save / Update admin PIN ───────────────────────────────────────────
  const saveReportPin = async () => {
    if (reportPin.length !== 4) { toast.error('Report PIN must be exactly 4 digits'); return; }
    if (reportPin !== confirmReportPin) { toast.error('Report PINs do not match'); return; }
    setSavingReportPin(true);
    try {
      const res = await fetch(SALON_API_URL + '/salon-users/me/report-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ pin: reportPin }),
      });
      const data = await res.json();
      if (data.success) {
        setReportPin('');
        setConfirmReportPin('');
        // Clear cached report sessions so they must enter the new PIN next time (no staleness)
        localStorage.removeItem('salon_report_access_sales');
        localStorage.removeItem('salon_report_access_expenses');
        localStorage.removeItem('salon_report_access_attendant');
        sessionStorage.removeItem('salon_report_access_sales');
        sessionStorage.removeItem('salon_report_access_expenses');
        sessionStorage.removeItem('salon_report_access_attendant');
        toast.success('Your report PIN is saved');
      } else {
        toast.error(data.message || 'Failed to save report PIN');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSavingReportPin(false);
    }
  };

  const saveAdminPin = async () => {
    if (newPin.length !== 4) { toast.error('PIN must be exactly 4 digits'); return; }
    if (newPin !== confirmPin) { toast.error('PINs do not match'); return; }
    // Updating existing PIN — OTP on admin email required
    if (hasAdminPin && !updateOtpVerified) { toast.error('Verify the OTP sent to your admin email first'); return; }
    // First-time set — email must be verified from Section 1
    if (!hasAdminPin && !otpVerified && !savedEmail) { toast.error('Verify your admin email first'); return; }
    setSavingPin(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/set-pin`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ pin: newPin }),
      });
      const data = await res.json();
      if (data.success) {
        setHasAdminPin(true); setNewPin(''); setConfirmPin('');
        setUpdateOtpSent(false); setUpdateOtpVerified(false); setUpdateOtp('');
        // Mark as unlocked for 12 hours
        localStorage.setItem('salonAdminPinUnlockedTime', Date.now().toString());
        setVisibilityUnlocked(true);
        toast.success(hasAdminPin ? 'Admin PIN updated!' : 'Admin PIN set! You can now use it to login.');
      } else toast.error(data.message || 'Failed to save PIN');
    } catch(e) { toast.error('Network error'); }
    finally { setSavingPin(false); }
  };

  // ── Verify admin PIN for visibility changes ───────────────────────────
  const verifyVisibilityPin = async () => {
    const pin = visibilityPin.join('');
    if (pin.length !== 4) { setVisPinError('Enter your 4-digit admin PIN'); return; }
    setVerifyingVisPin(true); setVisPinError('');
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/verify-pin`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (data.success) {
        setVisibilityUnlocked(true);
        // Save the timestamp to localStorage
        localStorage.setItem('salonAdminPinUnlockedTime', Date.now().toString());
        setShowVisibilityPin(false);
        setVisibilityPin(['', '', '', '']);
        // Immediately save after PIN verified
        await doSaveReportVisibility();
      } else {
        setVisPinError('Incorrect PIN. Try again.');
        setVisibilityPin(['', '', '', '']);
      }
    } catch(e) { setVisPinError('Network error. Try again.'); }
    finally { setVerifyingVisPin(false); }
  };

  // ── Core save (called directly if already admin, or after PIN verify) ─
  const doSaveReportVisibility = async () => {
    setSavingReports(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/report-visibility`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ reportAccess }),
      });
      const data = await res.json();
      if (data.success) toast.success('Report access settings saved!');
      else toast.error(data.message || 'Failed to save');
    } catch(e) { toast.error('Network error'); }
    finally { setSavingReports(false); }
  };

  // ── Save report visibility ────────────────────────────────────────────
  const updateReportRule = (reportId, patch) => {
    setReportAccess((prev) => ({
      ...prev,
      [reportId]: { ...prev[reportId], ...patch },
    }));
  };

  const toggleReportRole = (reportId, roleId) => {
    setReportAccess((prev) => {
      const current = prev[reportId] || DEFAULT_REPORT_ACCESS[reportId];
      const currentRoles = Array.isArray(current.allowedRoles) ? current.allowedRoles : [];
      const allowedRoles = currentRoles.includes(roleId)
        ? currentRoles.filter((role) => role !== roleId)
        : [...currentRoles, roleId];
      return {
        ...prev,
        [reportId]: {
          ...current,
          visibility: 'selected_roles',
          allowedRoles,
        },
      };
    });
  };

  const saveReportVisibility = async () => {
    // Admin session or already unlocked this session → save directly
    if (isSalonAdmin || visibilityUnlocked || !hasAdminPin) {
      await doSaveReportVisibility();
    } else {
      // Need PIN verification first
      setVisibilityPin(['', '', '', '']);
      setVisPinError('');
      setShowVisibilityPin(true);
    }
  };

  // ── Send OTP for delete/reset ─────────────────────────────────────────
  const sendDeleteOtp = async () => {
    if (!savedEmail) { toast.error('No admin email set. Set one first.'); return; }
    setDeletingOtp(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/admin-settings/send-otp`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ email: savedEmail, purpose: deleteMode }),
      });
      const data = await res.json();
      if (data.success) { setDeleteOtpSent(true); setDeleteOtp(''); startCooldown(setDeleteCooldown); toast.success(`OTP sent to ${savedEmail}`); }
      else toast.error(data.message || 'Failed to send OTP');
    } catch(e) { toast.error('Network error'); }
    finally { setDeletingOtp(false); }
  };

  // ── Confirm delete/reset with OTP ────────────────────────────────────
  const confirmDeleteReset = async () => {
    if (deleteOtp.length < 6) { toast.error('Enter the 6-digit OTP'); return; }
    setConfirming(true);
    const endpoint = deleteMode === 'reset' ? '/admin-settings/reset-pin' : '/admin-settings/delete-admin';
    try {
      const res  = await fetch(`${SALON_API_URL}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ otp: deleteOtp, email: savedEmail }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(deleteMode === 'reset' ? 'Admin PIN reset!' : 'Admin credentials deleted!');
        setDeleteMode(null); setDeleteOtp(''); setDeleteOtpSent(false);
        if (deleteMode === 'delete') { setHasAdminPin(false); setSavedEmail(''); setAdminEmail(''); setOtpVerified(false); }
        else { setHasAdminPin(false); setNewPin(''); setConfirmPin(''); }
        fetchSettings();
      } else toast.error(data.message || 'Wrong OTP or failed');
    } catch(e) { toast.error('Network error'); }
    finally { setConfirming(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={36} className="animate-spin text-purple-600"/>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Salon Profile', icon: Building2 },
    { id: 'security', label: 'Security & PINs', icon: Lock },
    { id: 'access', label: 'User Access', icon: Users },
  ];
  if (isOwner) {
    tabs.push({ id: 'reports', label: 'Report Controls', icon: BarChart3 });
  }
  tabs.push({ id: 'danger', label: 'Danger Zone', icon: AlertTriangle, danger: true });

  return (
    <div className="p-6 lg:p-8 w-full min-h-full">
      <ToastContainer position="top-right" autoClose={4000}/>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-black text-gray-900 dark:text-white font-sans tracking-tight">Salon Settings</h1>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Configure your salon profile, security PINs, member access permissions, and reports.</p>
      </div>

      {/* Tab Navigation Topbar */}
      <div className="flex border-b border-slate-200 dark:border-slate-800 gap-1 overflow-x-auto pb-px scrollbar-none mb-8">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition-all -mb-px whitespace-nowrap ${
                isActive
                  ? tab.danger
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-purple-600 text-purple-700 dark:text-purple-300 font-extrabold'
                  : tab.danger
                  ? 'border-transparent text-red-400 hover:text-red-500 hover:border-red-300'
                  : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-700'
              }`}
            >
              <TabIcon size={16} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content Panel */}
      <div className="w-full">
          {activeTab === 'profile' && (
            <div className="space-y-6 w-full animate-fade-in">
              <Section title="Salon Info" desc="Used across the salon billing header and salon screens" icon={Building2} color="blue">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Salon Name</label>
                    <input
                      type="text"
                      value={salonProfile.salonName}
                      onChange={e => setSalonProfile(prev => ({ ...prev, salonName: e.target.value }))}
                      placeholder="Enter salon name"
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Contact Person <span className="normal-case font-normal text-gray-400">(optional)</span></label>
                    <div className="relative">
                      <UserRound size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"/>
                      <input
                        type="text"
                        value={salonProfile.contactPersonName}
                        onChange={e => setSalonProfile(prev => ({ ...prev, contactPersonName: e.target.value }))}
                        placeholder="Owner or manager name"
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Contact Number <span className="normal-case font-normal text-gray-400">(optional)</span></label>
                    <div className="relative">
                      <Phone size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"/>
                      <input
                        type="tel"
                        value={salonProfile.contactPhone}
                        onChange={e => setSalonProfile(prev => ({ ...prev, contactPhone: e.target.value }))}
                        placeholder="Salon contact number"
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                      />
                    </div>
                  </div>
                  <button
                    onClick={saveSalonProfile}
                    disabled={savingProfile}
                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 shadow-sm transition-all active:scale-98"
                  >
                    {savingProfile ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                    Save Salon Info
                  </button>
                </div>
              </Section>

              <Section
                title="Language Settings"
                desc="Set your preferred user interface language."
                icon={Globe}
                color="blue"
              >
                <div className="space-y-3">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block">
                    Preferred Language
                  </label>
                  <select
                    value={language}
                    onChange={e => handleLanguageChange(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white cursor-pointer hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi (हिंदी)</option>
                    <option value="pa">Punjabi (ਪੰਜਾਬੀ)</option>
                    <option value="bn">Bengali (বাংলা)</option>
                    <option value="mr">Marathi (मराठी)</option>
                    <option value="gu">Gujarati (ગુજરાતી)</option>
                    <option value="ta">Tamil (தமிழ்)</option>
                    <option value="te">Telugu (తెలుగు)</option>
                    <option value="kn">Kannada (ಕನ್ನಡ)</option>
                    <option value="ml">Malayalam (മലയാളം)</option>
                    <option value="or">Odia (ଓଡ଼ିଆ)</option>
                    <option value="as">Assamese (অসমীয়া)</option>
                    <option value="ur">Urdu (اردو)</option>
                  </select>
                </div>
              </Section>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6 w-full animate-fade-in">
                {/* ── 1. Admin Email ─────────────────────────────────────────── */}
                <Section title="Admin Email" desc="OTP will be sent here for PIN setup and sensitive actions" icon={Mail} color="purple">
                  <div className="space-y-4">
                    {savedEmail && (
                      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
                        <CheckCircle2 size={16} className="text-green-500 flex-shrink-0"/>
                        <div>
                          <p className="text-sm font-bold text-green-700 dark:text-green-400">Verified admin email</p>
                          <p className="text-xs text-green-600 dark:text-green-500">{savedEmail}</p>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
                        {savedEmail ? 'Change Email' : 'Set Admin Email'}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={adminEmail}
                          onChange={e => { setAdminEmail(e.target.value); setOtpSent(false); setOtpVerified(false); setEmailOtp(''); }}
                          placeholder="admin@yoursalon.com"
                          className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-gray-800 dark:text-white hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                        />
                        <button
                          onClick={sendEmailOtp}
                          disabled={sendingOtp || otpCooldown > 0 || (adminEmail === savedEmail && !!savedEmail)}
                          className="px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center gap-2 flex-shrink-0 transition-colors"
                        >
                          {sendingOtp ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                          {otpCooldown > 0 ? `${otpCooldown}s` : otpSent ? 'Resend' : 'Send OTP'}
                        </button>
                      </div>
                    </div>

                    {otpSent && !otpVerified && (
                      <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-100 dark:border-purple-900/50">
                        <p className="text-sm font-semibold text-purple-700 dark:text-purple-300 text-center">
                          Enter the 6-digit OTP sent to <strong>{adminEmail}</strong>
                        </p>
                        <OtpInput length={6} value={emailOtp} onChange={setEmailOtp}/>
                        <button
                          onClick={verifyEmailOtp}
                          disabled={verifyingOtp || emailOtp.length < 6}
                          className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                        >
                          {verifyingOtp ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                          Verify OTP
                        </button>
                      </div>
                    )}

                    {otpVerified && (
                      <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
                        <CheckCircle2 size={16} className="text-green-500"/>
                        <p className="text-sm font-bold text-green-700 dark:text-green-400">Email verified! Now set your admin PIN below.</p>
                      </div>
                    )}
                  </div>
                </Section>

                {/* ── 2. Admin PIN ───────────────────────────────────────────── */}
                {isOwner && (
                  <Section
                    title={hasAdminPin ? 'Change Admin PIN' : 'Set Admin PIN'}
                    desc="This PIN can be used to login instead of OTP. Keep it private."
                    icon={KeyRound}
                    color="purple"
                  >
                    {hasAdminPin && (
                      <div className="flex items-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl mb-4">
                        <Shield size={16} className="text-purple-500 flex-shrink-0"/>
                        <div>
                          <p className="text-sm font-bold text-purple-700 dark:text-purple-300">Admin PIN is active</p>
                          <p className="text-xs text-purple-600 dark:text-purple-400">Verify the OTP sent to your admin email below before changing it.</p>
                        </div>
                      </div>
                    )}

                    {!savedEmail && !otpVerified ? (
                      <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                        <AlertTriangle size={16} className="text-amber-500 flex-shrink-0"/>
                        <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">Verify your admin email above before setting a PIN</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* OTP gate — only shown when updating an existing PIN */}
                        {hasAdminPin && !updateOtpVerified && (
                          <div className="space-y-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-2xl border border-orange-100 dark:border-orange-850">
                            <div className="flex items-center gap-2">
                              <AlertTriangle size={15} className="text-orange-500 flex-shrink-0"/>
                              <p className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                                Confirm your identity before changing PIN
                              </p>
                            </div>
                            <p className="text-xs text-orange-600 dark:text-orange-400">
                              An OTP will be sent to <strong>{savedEmail}</strong>
                            </p>
                            {!updateOtpSent ? (
                              <button
                                onClick={sendUpdateOtp}
                                disabled={sendingUpdateOtp || updateOtpCooldown > 0}
                                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                              >
                                {sendingUpdateOtp ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                                {updateOtpCooldown > 0 ? `Resend in ${updateOtpCooldown}s` : 'Send OTP to Admin Email'}
                              </button>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-xs font-semibold text-orange-700 dark:text-orange-300 text-center">
                                  Enter the 6-digit OTP sent to <strong>{savedEmail}</strong>
                                </p>
                                <OtpInput length={6} value={updateOtp} onChange={setUpdateOtp}/>
                                <button
                                  onClick={verifyUpdateOtp}
                                  disabled={verifyingUpdateOtp || updateOtp.length < 6}
                                  className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                                >
                                  {verifyingUpdateOtp ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                                  Verify OTP
                                </button>
                                <button
                                  onClick={sendUpdateOtp}
                                  disabled={sendingUpdateOtp || updateOtpCooldown > 0}
                                  className="w-full py-1.5 text-xs text-orange-500 hover:text-orange-700 flex items-center justify-center gap-1"
                                >
                                  <RefreshCw size={11}/> {updateOtpCooldown > 0 ? `Resend in ${updateOtpCooldown}s` : 'Resend OTP'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Show verified banner */}
                        {hasAdminPin && updateOtpVerified && (
                          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
                            <CheckCircle2 size={16} className="text-green-500"/>
                            <p className="text-sm font-bold text-green-700 dark:text-green-400">Identity verified — enter your new PIN below</p>
                          </div>
                        )}

                        {/* Only show PIN fields if: first-time set, OR update OTP verified */}
                        {(!hasAdminPin || updateOtpVerified) && (
                          <>
                            <PinInput label="New PIN (4 digits)" value={newPin} onChange={setNewPin}
                              show={showPin} onToggle={() => setShowPin(p=>!p)} placeholder="Enter new PIN"/>
                            <PinInput label="Confirm PIN" value={confirmPin} onChange={setConfirmPin}
                              show={showConfirm} onToggle={() => setShowConfirm(p=>!p)} placeholder="Confirm PIN"/>

                            {newPin && confirmPin && newPin !== confirmPin && (
                              <div className="flex items-center gap-2 text-red-500 text-xs font-semibold">
                                <XCircle size={14}/> PINs do not match
                              </div>
                            )}
                            {newPin && confirmPin && newPin === confirmPin && newPin.length === 4 && (
                              <div className="flex items-center gap-2 text-green-500 text-xs font-semibold">
                                <CheckCircle2 size={14}/> PINs match
                              </div>
                            )}

                            <button
                              onClick={saveAdminPin}
                              disabled={savingPin || newPin.length !== 4 || newPin !== confirmPin}
                              className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                            >
                              {savingPin ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                              {hasAdminPin ? 'Update Admin PIN' : 'Set Admin PIN'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </Section>
                )}
                {/* Your Report PIN */}
                <Section
                  title="Your Report PIN"
                  desc="Set your own 4-digit PIN for protected reports."
                  icon={Lock}
                  color="blue"
                >
                  <div className="space-y-4">
                    <PinInput label="Report PIN (4 digits)" value={reportPin} onChange={setReportPin}
                      show={showPin} onToggle={() => setShowPin(p=>!p)} placeholder="Enter report PIN"/>
                    <PinInput label="Confirm Report PIN" value={confirmReportPin} onChange={setConfirmReportPin}
                      show={showConfirm} onToggle={() => setShowConfirm(p=>!p)} placeholder="Confirm report PIN"/>

                    {reportPin && confirmReportPin && reportPin !== confirmReportPin && (
                      <div className="flex items-center gap-2 text-red-500 text-xs font-semibold">
                        <XCircle size={14}/> Report PINs do not match
                      </div>
                    )}

                    <button
                      onClick={saveReportPin}
                      disabled={savingReportPin || reportPin.length !== 4 || reportPin !== confirmReportPin}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                      {savingReportPin ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                      Save My Report PIN
                    </button>
                  </div>
                </Section>

                {/* Manager Permissions Settings */}
                {isOwner && hasAdminPin && (
                  <Section
                    title="Manager Permissions Settings"
                    desc="Configure what features the manager can access when they log in."
                    icon={Users}
                    color="purple"
                  >
                    <div className="space-y-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="managerAccessMode"
                          checked={managerFullAccess}
                          onChange={() => setManagerFullAccess(true)}
                          className="w-4 h-4 text-purple-600 accent-purple-600 mt-0.5 cursor-pointer"
                        />
                        <div>
                          <span className="text-sm font-bold text-gray-800 dark:text-white">Give Full Access to Manager</span>
                          <p className="text-xs text-gray-500">Manager will have access to all modules and features</p>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="managerAccessMode"
                          checked={!managerFullAccess}
                          onChange={() => setManagerFullAccess(false)}
                          className="w-4 h-4 text-purple-600 accent-purple-600 mt-0.5 cursor-pointer"
                        />
                        <div>
                          <span className="text-sm font-bold text-gray-800 dark:text-white">Limit Manager Access</span>
                          <p className="text-xs text-gray-500">Select features to restrict manager from accessing</p>
                        </div>
                      </label>

                      {!managerFullAccess && (
                        <div className="bg-gray-50 dark:bg-slate-800/40 rounded-2xl p-4 border border-slate-150 dark:border-slate-700 space-y-2">
                          <p className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-2">
                            Block Features:
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            {MANAGER_FEATURES.map(feat => {
                              const isBlocked = restrictedFeatures.includes(feat.id);
                              return (
                                <label key={feat.id} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
                                  <input
                                    type="checkbox"
                                    checked={isBlocked}
                                    onChange={() => toggleFeatureRestriction(feat.id)}
                                    className="w-4 h-4 rounded text-purple-600 accent-purple-600 cursor-pointer"
                                  />
                                  <span className={`text-xs font-semibold ${isBlocked ? 'text-gray-900 dark:text-white font-bold' : 'text-gray-600 dark:text-gray-400'}`}>
                                    {feat.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => triggerVerification(handleSaveManagerPermissions)}
                        disabled={savingManagerPerms}
                        className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                      >
                        {savingManagerPerms ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                        Save Manager Permissions
                      </button>
                    </div>
                  </Section>
                )}
            </div>
          )}

          {activeTab === 'access' && (
            <div className="max-w-none animate-fade-in">
              <SalonUserAccessSection />
            </div>
          )}

          {activeTab === 'reports' && isOwner && (
            <div className="max-w-none animate-fade-in">
              {/* ── 3. Report Visibility ────────────────────────────────────── */}
              <Section
                title="Report Access Control"
                desc="Only reports are protected. All other salon modules stay open for every role."
                icon={BarChart3}
                color="blue"
              >
                <div className="space-y-4">
                  {hasAdminPin && !isSalonAdmin && !visibilityUnlocked && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-850">
                      <Lock size={15} className="text-amber-500 flex-shrink-0"/>
                      <p className="text-xs text-amber-700 dark:text-amber-300 font-medium flex-1">
                        Enter the admin PIN once to change report rules. Viewing everything except reports stays open.
                      </p>
                    </div>
                  )}
                  {(isSalonAdmin || visibilityUnlocked || !hasAdminPin) && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-2xl">
                      <Unlock size={14} className="text-green-500 flex-shrink-0"/>
                      <p className="text-xs text-green-700 dark:text-green-400 font-semibold">You can set who sees each report and whether that report asks for a personal report PIN.</p>
                    </div>
                  )}

                  {REPORT_PAGES.map((page) => {
                    const rule = reportAccess[page.id] || DEFAULT_REPORT_ACCESS[page.id];
                    const selectedCount = (rule.allowedRoles || []).length;
                    return (
                      <div key={page.id} className="rounded-3xl border border-slate-150 dark:border-slate-800 p-5 space-y-4 bg-gray-50/70 dark:bg-slate-800/40">
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-2xl bg-white dark:bg-slate-700 border border-gray-100 dark:border-slate-600 flex items-center justify-center flex-shrink-0">
                            <page.icon size={18} className="text-purple-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-black text-gray-900 dark:text-white">{page.label}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{page.desc}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            type="button"
                            disabled={hasAdminPin && !isSalonAdmin && !visibilityUnlocked}
                            onClick={() => updateReportRule(page.id, { visibility: 'everyone', allowedRoles: [] })}
                            className={`px-3 py-2.5 rounded-2xl border text-sm font-bold transition-all ${rule.visibility === 'everyone'
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300'} ${hasAdminPin && !isSalonAdmin && !visibilityUnlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            Open to everyone
                          </button>
                          <button
                            type="button"
                            disabled={hasAdminPin && !isSalonAdmin && !visibilityUnlocked}
                            onClick={() => updateReportRule(page.id, { visibility: 'selected_roles', allowedRoles: rule.allowedRoles || [] })}
                            className={`px-3 py-2.5 rounded-2xl border text-sm font-bold transition-all ${rule.visibility === 'selected_roles'
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300'} ${hasAdminPin && !isSalonAdmin && !visibilityUnlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            Selected roles only
                          </button>
                        </div>

                        {rule.visibility === 'selected_roles' && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Allowed Roles</p>
                              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{selectedCount} selected</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {REPORT_ROLE_OPTIONS.map((role) => {
                                const active = (rule.allowedRoles || []).includes(role.id);
                                return (
                                  <button
                                    key={role.id}
                                    type="button"
                                    disabled={hasAdminPin && !isSalonAdmin && !visibilityUnlocked}
                                    onClick={() => toggleReportRole(page.id, role.id)}
                                    className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${active
                                      ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300'
                                      : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-300'} ${hasAdminPin && !isSalonAdmin && !visibilityUnlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                  >
                                    {role.label}
                                  </button>
                                );
                              })}
                            </div>
                            {selectedCount === 0 && (
                              <p className="text-xs text-rose-500 font-semibold">Pick at least one role before saving this rule.</p>
                            )}
                          </div>
                        )}

                        <ToggleRow
                          icon={KeyRound}
                          label="Ask for each user's own report PIN"
                          desc="Users in allowed roles must unlock this report with their own 4-digit report PIN."
                          checked={!!rule.requirePin}
                          onChange={(value) => updateReportRule(page.id, { requirePin: value })}
                          disabled={hasAdminPin && !isSalonAdmin && !visibilityUnlocked}
                        />
                      </div>
                    );
                  })}

                  <div className="pt-1">
                    <button
                      onClick={saveReportVisibility}
                      disabled={savingReports || Object.values(reportAccess).some((rule) => rule.visibility === 'selected_roles' && !(rule.allowedRoles || []).length)}
                      className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                      {savingReports
                        ? <Loader2 size={14} className="animate-spin"/>
                        : (hasAdminPin && !isSalonAdmin && !visibilityUnlocked)
                          ? <Lock size={14}/>
                          : <Save size={14}/>
                      }
                      {(hasAdminPin && !isSalonAdmin && !visibilityUnlocked)
                        ? 'Save (Admin PIN Required)'
                        : 'Save Report Rules'
                      }
                    </button>
                  </div>

                  <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl mt-1">
                    <Lock size={14} className="text-blue-500 flex-shrink-0 mt-0.5"/>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Users can keep a report unlocked on their own device, so they do not need to enter their report PIN again every time.
                    </p>
                  </div>
                </div>
              </Section>
            </div>
          )}

          {activeTab === 'danger' && (
            <div className="w-full animate-fade-in">
              {/* ── 4. Danger Zone ─────────────────────────────────────────── */}
              <Section title="Danger Zone" desc="Irreversible actions — OTP verification required" icon={AlertTriangle} color="red">
                <div className="space-y-3">
                  {/* Reset PIN */}
                  <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-2xl border border-orange-100 dark:border-orange-950/20">
                    <div>
                      <p className="text-sm font-bold text-gray-800 dark:text-white">Reset Admin PIN</p>
                      <p className="text-xs text-gray-500">Remove current PIN (you can set a new one after)</p>
                    </div>
                    <button
                      onClick={() => { setDeleteMode('reset'); setDeleteOtp(''); setDeleteOtpSent(false); }}
                      disabled={!hasAdminPin}
                      className="px-4 py-2 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded-xl text-xs font-bold disabled:opacity-40 flex items-center gap-1.5 transition-colors"
                    >
                      <RotateCcw size={13}/> Reset PIN
                    </button>
                  </div>

                  {/* Delete admin */}
                  <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-950/20">
                    <div>
                      <p className="text-sm font-bold text-gray-800 dark:text-white">Delete Admin Setup</p>
                      <p className="text-xs text-gray-500">Removes admin email, PIN, and all access controls</p>
                    </div>
                    <button
                      onClick={() => { setDeleteMode('delete'); setDeleteOtp(''); setDeleteOtpSent(false); }}
                      disabled={!savedEmail && !hasAdminPin}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-xl text-xs font-bold disabled:opacity-40 flex items-center gap-1.5 transition-colors"
                    >
                      <Trash2 size={13}/> Delete
                    </button>
                  </div>
                </div>
              </Section>
            </div>
          )}
        </div>

      {/* ── Admin PIN verification modal for visibility changes ──────── */}
      {showVisibilityPin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100 dark:border-slate-800">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-6 py-7 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Shield size={26} className="text-white"/>
              </div>
              <h3 className="font-black text-white text-lg">Admin Verification</h3>
              <p className="text-blue-200 text-xs mt-1">Enter your 4-digit admin PIN to save visibility settings</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex gap-2 justify-center">
                {visibilityPin.map((d, i) => (
                  <input
                    key={i}
                    id={`vis-pin-${i}`}
                    type="password"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    autoFocus={i === 0}
                    disabled={verifyingVisPin}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '').slice(-1);
                      const next = [...visibilityPin]; next[i] = v;
                      setVisibilityPin(next);
                      if (v && i < 3) document.getElementById(`vis-pin-${i+1}`)?.focus();
                      if (i === 3 && v && next.every(d => d)) {
                        setTimeout(() => {
                          setVisibilityPin(next);
                        }, 50);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Backspace' && !d && i > 0)
                        document.getElementById(`vis-pin-${i-1}`)?.focus();
                    }}
                    className={`w-14 h-16 text-center text-xl font-bold border-2 rounded-xl outline-none transition-all
                      ${d ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700' : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-800 dark:text-white'}
                      focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-50`}
                  />
                ))}
              </div>

              {visPinError && (
                <div className="flex items-center justify-center gap-2 text-red-500 text-sm font-medium">
                  <XCircle size={14}/> {visPinError}
                </div>
              )}

              <button
                onClick={verifyVisibilityPin}
                disabled={verifyingVisPin || visibilityPin.join('').length !== 4}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-98"
              >
                {verifyingVisPin ? <Loader2 size={14} className="animate-spin"/> : <Shield size={14}/>}
                {verifyingVisPin ? 'Verifying…' : 'Verify & Save'}
              </button>
              <button
                onClick={() => { setShowVisibilityPin(false); setVisPinError(''); setVisibilityPin(['', '', '', '']); }}
                className="w-full py-2.5 rounded-xl border border-gray-250 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete / Reset OTP Modal ─────────────────────────────────── */}
      {deleteMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-6 border border-slate-100 dark:border-slate-800">
            <div className="text-center mb-5">
              <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center ${deleteMode === 'delete' ? 'bg-red-100 text-red-500' : 'bg-orange-100 text-orange-500'}`}>
                {deleteMode === 'delete' ? <Trash2 size={24}/> : <RotateCcw size={24}/>}
              </div>
              <h3 className="font-black text-gray-900 dark:text-white text-lg">
                {deleteMode === 'delete' ? 'Delete Admin Setup?' : 'Reset Admin PIN?'}
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                An OTP will be sent to <strong className="text-gray-600 dark:text-gray-300">{savedEmail}</strong> to confirm
              </p>
            </div>

            {!deleteOtpSent ? (
              <div className="space-y-3">
                <button
                  onClick={sendDeleteOtp}
                  disabled={deletingOtp || deleteCooldown > 0}
                  className={`w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-50 transition-colors
                    ${deleteMode === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-500 hover:bg-orange-600'}`}
                >
                  {deletingOtp ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                  {deleteCooldown > 0 ? `Wait ${deleteCooldown}s` : 'Send OTP to Email'}
                </button>
                <button onClick={() => setDeleteMode(null)}
                  className="w-full py-3 rounded-xl border border-gray-250 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-300">
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 text-center">Enter the 6-digit OTP</p>
                <OtpInput length={6} value={deleteOtp} onChange={setDeleteOtp}/>
                <button
                  onClick={confirmDeleteReset}
                  disabled={confirming || deleteOtp.length < 6}
                  className={`w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-50 transition-colors
                    ${deleteMode === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-500 hover:bg-orange-600'}`}
                >
                  {confirming ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                  {deleteMode === 'delete' ? 'Confirm Delete' : 'Confirm Reset'}
                </button>
                <button
                  onClick={sendDeleteOtp}
                  disabled={deletingOtp || deleteCooldown > 0}
                  className="w-full py-2 text-xs text-gray-400 hover:text-gray-650 flex items-center justify-center gap-1"
                >
                  <RefreshCw size={12}/> {deleteCooldown > 0 ? `Resend in ${deleteCooldown}s` : 'Resend OTP'}
                </button>
                <button onClick={() => { setDeleteMode(null); setDeleteOtpSent(false); setDeleteOtp(''); }}
                  className="w-full py-2.5 rounded-xl border border-gray-250 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-300">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manager Verification Modal */}
      {showManagerVerifyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100 dark:border-slate-800">
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-6 text-white relative">
              <button
                onClick={() => {
                  setShowManagerVerifyModal(false);
                  setOnVerifiedCallback(null);
                }}
                className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                <Lock size={22} />
              </div>
              <h2 className="text-lg font-bold text-white">Admin Security Verification</h2>
              <p className="text-white/70 text-xs mt-1">Identity verification is required to complete this action</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Method 1: Enter Admin PIN
                </label>
                <PinBoxes value={managerVerifyPin} onChange={setManagerVerifyPin} color="purple" />
              </div>

              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                <span className="text-[10px] font-black text-slate-400">OR</span>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                  Method 2: Email verification OTP
                </label>
                {!managerVerifyOtpSent ? (
                  <button
                    onClick={handleSendManagerVerifyOtp}
                    disabled={managerVerifyLoading}
                    className="w-full py-2.5 border-2 border-purple-600 text-purple-600 bg-purple-50/50 hover:bg-purple-50 font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1.5"
                  >
                    {managerVerifyLoading && <Loader2 size={13} className="animate-spin" />}
                    Send OTP to {savedEmail}
                  </button>
                ) : (
                  <div className="space-y-1.5">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={managerVerifyOtp}
                      onChange={e => setManagerVerifyOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP code"
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                    <p className="text-[10px] text-slate-400">Verification code sent to {savedEmail}</p>
                  </div>
                )}
              </div>

              {managerVerifyError && (
                <p className="text-xs text-red-650 font-bold text-center mt-2">{managerVerifyError}</p>
              )}

              <button
                onClick={handleManagerVerifyAction}
                disabled={managerVerifyLoading}
                className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 active:scale-98"
              >
                {managerVerifyLoading && <Loader2 size={16} className="animate-spin" />}
                {managerVerifyLoading ? 'Verifying...' : 'Verify & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
